import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { NotificationItem } from '@/api/generated/models'
import {
  getNotifications,
  putNotificationsIdRead,
  putNotificationsReadAll,
} from '@/api/generated/notifications-notifications/notifications-notifications'
import { Button } from '@/components/ui/button'
import { buildLookup } from './notification-render'
import { NotificationRow } from './NotificationRow'
import { decrementUnreadCount } from './unread-cache'

const RECENT_PAGE_SIZE = 12

// Desktop popover body: the ~12 most recent notifications. Opening it does NOT
// mark anything read — read happens only on row click-through or "Mark all
// read" (preserves the unread signal). onClose fires after navigation so the
// popover closes when a row is opened.
export function NotificationsDropdown({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const feedQuery = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: ({ signal }) => getNotifications({ pageSize: RECENT_PAGE_SIZE }, signal),
  })

  const data = feedQuery.data
  const items: NotificationItem[] = data?.items ?? []
  const lookup = useMemo(
    () => buildLookup(data?.players ?? [], data?.teams ?? [], data?.leagues ?? []),
    [data],
  )

  const markOne = useMutation({
    mutationFn: (id: number) => putNotificationsIdRead(id),
    // Drop the badge synchronously at click time — openItem navigates/closes and
    // unmounts this dropdown before the PUT resolves, so onSuccess below can't be
    // relied on for the badge (see decrementUnreadCount). Invalidations still run
    // to reconcile whenever we're not navigating away.
    onMutate: () => decrementUnreadCount(queryClient),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'recent'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'feed'] })
    },
  })

  const markAll = useMutation({
    mutationFn: () => putNotificationsReadAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unreadCount'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'recent'] })
      queryClient.invalidateQueries({ queryKey: ['notifications', 'feed'] })
    },
  })

  function openItem(item: NotificationItem) {
    // Same click-through pattern as the full /notifications page (Task 3):
    // mark read only if currently unread, fire-and-forget; navigate only if
    // the notification carries a link. NotificationItem.link/notificationId
    // types are `string | null | undefined` / `number` respectively — guard
    // before calling navigate() since it requires a string `To`.
    if (!item.read) markOne.mutate(item.notificationId)
    onClose()
    if (item.link) navigate(item.link)
  }

  return (
    <div className="flex max-h-[70vh] flex-col">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
        <span className="text-sm font-semibold text-slate-900">Notifications</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => markAll.mutate()}
          disabled={markAll.isPending}
        >
          Mark all read
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {feedQuery.isLoading ? (
          <p className="px-3 py-8 text-center text-sm text-slate-500">Loading…</p>
        ) : feedQuery.isError ? (
          <p className="px-3 py-8 text-center text-sm text-slate-500">
            Couldn&apos;t load notifications.
          </p>
        ) : items.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-slate-500">You&apos;re all caught up.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => (
              <li key={item.notificationId}>
                <NotificationRow item={item} lookup={lookup} onOpen={openItem} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-slate-200 px-3 py-2 text-right">
        <Link
          to="/notifications"
          onClick={onClose}
          className="text-sm font-medium text-blue-700 hover:underline"
        >
          See all →
        </Link>
      </div>
    </div>
  )
}
